import type { FC, PropsWithChildren } from 'react';
import { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { formatDate } from 'app/utils/date';
import { hasDetailedStats, isEbattle } from 'app/config/videogameSpecific.config.helper';
import { partitionArray } from 'app/utils/array';
import ArrowIcon from 'app/icons/arrow-min-right.svg';
import ConfigContext from 'app/common/ConfigContext';
import TeamAvatar from 'app/components/TeamAvatar';
import VideogameVersionTag from 'app/components/VideogameVersionTag';

import style from './style.module.scss';

interface Props {
  encounter: Encounter | null;
  opponentId: number | undefined;
  onSelect: (encounter: Encounter) => void;
  selectable?: boolean;
}

const EmptyFormEncounter: FC = () => <div className={style.emptyEncounter} />;

const FormEncounter: FC<Props> = ({ encounter, opponentId, onSelect, selectable = true }) => {
  const [[displayedTeam, ..._], [otherTeam, ...__]] = partitionArray(
    encounter?.opponents || [],
    opponent => (opponent.id === opponentId ? 0 : 1)
  );

  const { t } = useTranslation();

  const { lang } = useContext(ConfigContext);

  const winnerClassname = classNames(style.winnerIndicator, {
    [style.winner]: encounter?.winner_id === opponentId,
    [style.tie]: encounter?.winner_id === null
  });

  const isEBattle = encounter ? isEbattle(encounter.videogame_slug) : false;

  const isFixtureOnly =
    !encounter?.detailed_stats ||
    !hasDetailedStats(encounter.videogame_slug) ||
    encounter.games.every(game => !game.complete) ||
    encounter.games.every(game => game.forfeit);

  const tabIndexProps = !selectable ? { tabIndex: -1 } : {};

  const WrapperElement: FC<PropsWithChildren> = useCallback(
    ({ children }) =>
      isFixtureOnly ? (
        <div
          className={classNames(style.formEncounter, {
            [style.fixtureOnlyMatch]: isFixtureOnly,
            [style.ebattleEncounter]: isEBattle
          })}
        >
          {children}
        </div>
      ) : (
        <button
          className={style.formEncounter}
          onClick={() => onSelect(encounter)}
          type="button"
          {...tabIndexProps}
        >
          {children}
        </button>
      ),
    [selectable]
  );

  if (!encounter) {
    return <EmptyFormEncounter />;
  }

  if (!displayedTeam || !otherTeam) {
    return <EmptyFormEncounter />;
  }

  return (
    <WrapperElement>
      <span className={winnerClassname} />
      {!isEBattle && (
        <VideogameVersionTag name={encounter.videogame_title} size="small" returnEmpty />
      )}
      <span className={classNames(style.scores, { [style.ebattleScores]: isEBattle })}>
        <span
          className={classNames({ [style.lowestScore]: displayedTeam.score < otherTeam.score })}
        >
          {displayedTeam.score}
        </span>
        <span> - </span>
        <span
          className={classNames({ [style.lowestScore]: otherTeam.score < displayedTeam.score })}
        >
          {otherTeam.score}
        </span>
      </span>
      {isEBattle && <span className={style.ebattleSep}>•</span>}
      {!isEBattle && <TeamAvatar team={otherTeam} noAlt />}
      <span
        className={classNames(isEBattle ? style.ebattleOpponent : style.opponentName, {
          [style.lowestScore]: otherTeam.score < displayedTeam.score
        })}
      >
        {otherTeam.name}
      </span>
      {!isEBattle && (
        <>
          <span className={classNames(style.encounterDate, style.encounterDateShort)}>
            {formatDate(new Date(encounter.begin_at), lang, { year: '2-digit' })}
          </span>
          <span className={classNames(style.encounterDate, style.encounterDateLong)}>
            {formatDate(new Date(encounter.begin_at), lang, { year: 'numeric' })}
          </span>
          <span className={style.seeMore}>
            <span className={style.seeMoreText}>{t('all-see_more')}</span>
            <ArrowIcon className={style.arrow} />
          </span>
        </>
      )}
    </WrapperElement>
  );
};

FormEncounter.displayName = 'FormEncounter';

export default FormEncounter;
